import * as React from "react";
import type { Event } from "src/types";

type EventProviderProps = {
  children: React.ReactNode;
  /**
   * `event` - give `EventProvider` an immediate value, useful when event data
   * has been retrieved on the server through `getServerSideProps`
   */
  event?: Event;
};

type TEventContext = {
  event: Event;
  setEvent: React.Dispatch<React.SetStateAction<Event>>;
};

export const EventContext = React.createContext<TEventContext>(undefined);

/**
 *
 * `EventProvider` enables us to have an easy way of accessing the current
 * event metadata through the `useEvent` hook. Why we're not using Redux in
 * this scenario is due to the ability for us to immediately pass an event
 * into the `EventProvider` when server-side rendering the page, bypassing the
 * need to dispatch the event into the store once the client renders.
 */
export default function EventProvider(props: EventProviderProps) {
  const [event, setEvent] = React.useState(props.event);

  const contextValue = React.useMemo(() => {
    return {
      event,
      setEvent,
    };
  }, [event]);

  if (!event) {
    return null;
  }

  return (
    <EventContext.Provider value={contextValue}>
      {props.children}
    </EventContext.Provider>
  );
}
